/**
 * Dispute Constants
 * All constants related to dispute management
 */

// ────────────────────────────────────────────────────────────────────────────
// Dispute Status with UI Properties
// ────────────────────────────────────────────────────────────────────────────

export const DISPUTE_STATUS = {
  OPEN: {
    value: 'OPEN',
    label: 'Open',
    color: 'info',
    description: 'Newly created — awaiting admin triage'
  },
  UNDER_REVIEW: {
    value: 'UNDER_REVIEW',
    label: 'Under Review',
    color: 'warning',
    description: 'Admin is actively investigating'
  },
  ESCALATED: {
    value: 'ESCALATED',
    label: 'Escalated',
    color: 'error',
    description: 'Escalated to senior staff or external mediation'
  },
  RESOLVED_FOR_FILER: {
    value: 'RESOLVED_FOR_FILER',
    label: 'Resolved for Filer',
    color: 'success',
    description: 'Resolved in favour of the person who filed the dispute'
  },
  RESOLVED_FOR_RESPONDENT: {
    value: 'RESOLVED_FOR_RESPONDENT',
    label: 'Resolved for Respondent',
    color: 'success',
    description: 'Resolved in favour of the respondent'
  },
  RESOLVED_MUTUAL: {
    value: 'RESOLVED_MUTUAL',
    label: 'Resolved - Mutual Agreement',
    color: 'success',
    description: 'Resolved by mutual agreement or compromise'
  },
  DISMISSED: {
    value: 'DISMISSED',
    label: 'Dismissed',
    color: 'default',
    description: 'Dispute was frivolous, duplicate, or lacked evidence'
  }
};

// Helper to get status config
export const getDisputeStatusConfig = (status) => {
  return DISPUTE_STATUS[status] || {
    value: status,
    label: status,
    color: 'default',
    description: ''
  };
};

// ────────────────────────────────────────────────────────────────────────────
// Service Types (matches backend ServiceType enum)
// ────────────────────────────────────────────────────────────────────────────

export const SERVICE_TYPE = {
  ROSCA: 'ROSCA',
  FUNDRAISER: 'FUNDRAISER',
  EVENTS: 'EVENTS',
  OTHER_GROUPS: 'OTHER_GROUPS',
  USER_FINANCIAL_TRANSACTION: 'USER_FINANCIAL_TRANSACTION'
};

// ────────────────────────────────────────────────────────────────────────────
// Dispute Types (matches backend DisputeType enum)
// ────────────────────────────────────────────────────────────────────────────

export const DISPUTE_TYPE = {
  TRANSACTION: 'TRANSACTION',
  GROUP: 'GROUP',
  CAMPAIGN: 'CAMPAIGN',
  EVENT: 'EVENT',
  LOAN: 'LOAN',
  OTHER: 'OTHER'
};

export const DISPUTE_TYPE_LABELS = {
  TRANSACTION: 'Transaction',
  GROUP: 'Group',
  CAMPAIGN: 'Fundraiser Campaign',
  EVENT: 'Event',
  LOAN: 'Loan',
  OTHER: 'Other'
};

/**
 * Mirrors backend DisputeType.toServiceType()
 * @param {string} disputeType
 * @returns {string} ServiceType
 */
export const getServiceTypeFromDisputeType = (disputeType) => {
  switch (disputeType) {
    case DISPUTE_TYPE.GROUP:       return SERVICE_TYPE.ROSCA;
    case DISPUTE_TYPE.TRANSACTION: return SERVICE_TYPE.USER_FINANCIAL_TRANSACTION;
    case DISPUTE_TYPE.CAMPAIGN:    return SERVICE_TYPE.FUNDRAISER;
    case DISPUTE_TYPE.EVENT:       return SERVICE_TYPE.EVENTS;
    case DISPUTE_TYPE.LOAN:
    case DISPUTE_TYPE.OTHER:
    default:                       return SERVICE_TYPE.OTHER_GROUPS;
  }
};

// ────────────────────────────────────────────────────────────────────────────
// Dispute Reasons by Dispute Type (matches backend DisputeReason enum)
// ────────────────────────────────────────────────────────────────────────────

export const DISPUTE_REASONS = {
  TRANSACTION: [
    { value: 'UNAUTHORIZED_DEDUCTION', label: 'Unauthorized Deduction' },
    { value: 'INCORRECT_AMOUNT', label: 'Incorrect Amount' },
    { value: 'DUPLICATE_CHARGE', label: 'Duplicate Charge' },
    { value: 'PAYMENT_NOT_RECEIVED', label: 'Payment Not Received' },
    { value: 'REFUND_NOT_PROCESSED', label: 'Refund Not Processed' },
    { value: 'FUND_REQUEST_ISSUE', label: 'Fund Request Issue' },
    { value: 'OTHER', label: 'Other' }
  ],
  GROUP: [
    { value: 'MISSED_PAYOUT', label: 'Missed Payout' },
    { value: 'INCORRECT_AMOUNT', label: 'Incorrect Amount' },
    { value: 'UNAUTHORIZED_DEDUCTION', label: 'Unauthorized Deduction' },
    { value: 'MEMBERSHIP_FEE_ISSUE', label: 'Membership Fee Issue' },
    { value: 'FUND_REQUEST_ISSUE', label: 'Fund Request Issue' },
    { value: 'OTHER', label: 'Other' }
  ],
  CAMPAIGN: [
    { value: 'FUNDS_MISUSE', label: 'Misuse of Funds' },
    { value: 'DONATION_NOT_REFLECTED', label: 'Donation Not Reflected' },
    { value: 'CAMPAIGN_FRAUD', label: 'Campaign Fraud' },
    { value: 'OTHER', label: 'Other' }
  ],
  EVENT: [
    { value: 'EVENT_CANCELLED_NO_REFUND', label: 'Event Cancelled - No Refund' },
    { value: 'TICKET_NOT_HONORED', label: 'Ticket Not Honored' },
    { value: 'EVENT_MISREPRESENTATION', label: 'Event Misrepresentation' },
    { value: 'OTHER', label: 'Other' }
  ],
  LOAN: [
    { value: 'OTHER', label: 'Other' }
  ],
  OTHER: [
    { value: 'MEMBERSHIP_FEE_ISSUE', label: 'Membership Fee Issue' },
    { value: 'UNAUTHORIZED_DEDUCTION', label: 'Unauthorized Deduction' },
    { value: 'FUND_REQUEST_ISSUE', label: 'Fund Request Issue' },
    { value: 'OTHER', label: 'Other' }
  ]
};

// Helper to get reason label
export const getDisputeReasonLabel = (reason) => {
  for (const disputeType in DISPUTE_REASONS) {
    const found = DISPUTE_REASONS[disputeType].find(r => r.value === reason);
    if (found) return found.label;
  }
  return reason;
};

// ────────────────────────────────────────────────────────────────────────────
// Dispute Form Validation
// ────────────────────────────────────────────────────────────────────────────

export const DISPUTE_VALIDATION = {
  TITLE: {
    MIN_LENGTH: 10,
    MAX_LENGTH: 200,
    REQUIRED: true
  },
  DESCRIPTION: {
    MIN_LENGTH: 50,
    MAX_LENGTH: 5000,
    REQUIRED: true
  },
  ATTACHMENTS: {
    MAX_COUNT: 5,
    MAX_SIZE_MB: 10,
    ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'image/gif']
  }
};

// ────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ────────────────────────────────────────────────────────────────────────────

/**
 * Check if dispute is in resolved state
 * @param {string} status - Dispute status
 * @returns {boolean}
 */
export const isDisputeResolved = (status) => {
  return ['RESOLVED_FOR_FILER', 'RESOLVED_FOR_RESPONDENT', 'RESOLVED_MUTUAL', 'DISMISSED'].includes(status);
};

/**
 * Check if dispute is still open/active
 * @param {string} status - Dispute status
 * @returns {boolean}
 */
export const isDisputeActive = (status) => {
  return ['OPEN', 'UNDER_REVIEW', 'ESCALATED'].includes(status);
};

/**
 * Get available reasons for a dispute type
 * @param {string} disputeType - Dispute type
 * @returns {Array} List of reason options
 */
export const getReasonsForDisputeType = (disputeType) => {
  return DISPUTE_REASONS[disputeType] || DISPUTE_REASONS.OTHER;
};

/**
 * Format dispute number for display
 * @param {string} disputeNumber - Dispute number
 * @returns {string} Formatted dispute number
 */
export const formatDisputeNumber = (disputeNumber) => {
  if (!disputeNumber) return 'N/A';
  return disputeNumber;
};
